import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { fetchUsers, bulkProcessUsers } from "../../services/apiService"; 
import permissionsConfig from '../../utils/permission.json';

const ImportAccount = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [allUsers, setAllUsers] = useState(new Map());

    useEffect(() => {
        const loadAllUsers = async () => {
            try {
                const users = await fetchUsers();
                const userMap = new Map();
                users.forEach(user => {
                    userMap.set(user.email, user);
                });
                setAllUsers(userMap);
            } catch (error) {
                console.error('Error fetching all users:', error);
            }
        };

        loadAllUsers();
    }, []);

    const handleChooseFile = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        setSelectedFile(file);
    };

    const handleGenerateTemplate = () => {
        const templateUrl = 'https://mura-ai-images-dev.s3.eu-west-1.amazonaws.com/Uploads/Template/Account+List+Template.xlsx';
        const filename = 'Worksheet in AI System (ODS & ADS)_20251112.xlsx';
        const link = document.createElement('a');
        link.href = templateUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getPermissionsForUserType = (userTypeEnum) => {
        if (!userTypeEnum) return {};

        const permissions = {};
        Object.keys(permissionsConfig).forEach(permission => {
            // Use the backend enum value directly
            if (permissionsConfig[permission].hasOwnProperty(userTypeEnum)) {
                permissions[permission] = permissionsConfig[permission][userTypeEnum] || false;
            }
        });
        return permissions;
    };



const handleImport = async () => {
    if (!selectedFile) {
        Swal.fire({
            icon: 'warning',
            title: 'No file selected',
            text: 'Please choose an Excel file first!'
        });
        return;
    }
    if (window.isImporting) {
        console.log('Import already in progress');
        return;
    }
    window.isImporting = true;

    try {
        const reader = new FileReader();
        reader.onload = async (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            if (jsonData.length === 0) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Empty file',
                    text: 'The selected file contains no data rows.'
                });
                window.isImporting = false;
                return;
            }

            // Quick pre-check: Ensure required columns exist (don't full validate here to keep client fast)
            const requiredColumns = ['Email Address', 'Account Status', 'User Type', 'User Name', 'Company Info'];
            const missingColumns = requiredColumns.filter(col => !jsonData[0].hasOwnProperty(col));
            if (missingColumns.length > 0) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid file structure',
                    text: `Missing required columns: ${missingColumns.join(', ')}`
                });
                window.isImporting = false;
                return;
            }

            try {
                const result = await bulkProcessUsers(selectedFile);

                window.isImporting = false;
                const { created, updated, totalProcessed, errors } = result;

                let swalConfig = {
                    title: 'Import completed successfully!',
                    icon: 'success',
                    //footer: `Created: ${created} | Updated: ${updated} | Total: ${totalProcessed}`
                };

                if (errors && errors.length > 0) {
                    swalConfig.icon = 'warning';
                    swalConfig.title = 'Import completed with issues';
                    swalConfig.html = `
                        <div style="max-height: 400px; overflow-y: auto;">
                            
                            <ul style="list-style-type: disc; padding-left: 20px; margin: 0;">
                                ${errors.map(error => `<li>${error}</li>`).join('')}
                            </ul>
                        </div>
                        <br><em>welcome emails with generated passwords to new users.</em>
                    `;
                } else if (created > 0) {
                    swalConfig.html = `
                        <strong>Processed:</strong> Created ${created}, Updated ${updated}, Total ${totalProcessed}<br><br>
                        <em> welcome emails with generated passwords to new users.</em>
                    `;
                }

                Swal.fire(swalConfig).then((result) => {
                    if (result.isConfirmed) {
                        navigate('/account-management');
                    }
                });
                setSelectedFile(null);

            } catch (uploadError) {
                console.error('Upload error:', uploadError);
                window.isImporting = false;
                Swal.fire({
                    icon: 'error',
                    title: 'Import failed',
                    text: uploadError.message || 'Failed to process file on server. Check console for details.'
                });
            }
        };

        reader.onerror = () => {
            throw new Error('Failed to read file');
        };

        reader.readAsArrayBuffer(selectedFile);

    } catch (error) {
        console.error('Error during import:', error);
        window.isImporting = false;
        Swal.fire({
            icon: 'error',
            title: 'Import failed',
            text: 'Error reading file: ' + error.message
        });
    }
};


return (
        <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6 lg:p-6 ">
            {/* Header */}
            <div className="flex items-center mb-15 md:mb-30 ">
                <button
                   onClick={() => navigate('/account-management')}
                    className="flex items-center text-gray-400 hover:text-white mr-2 md:mr-4"
                >
                    <ArrowLeft className="w-5 h-5 md:w-7 md:h-7 text-white strokeWidth={1}" />
                </button>
                 <h1 className="text-lg md:text-2xl lg:text-2xl font-semibold text-center md:text-left flex-1 -translate-x-3 md:translate-x-0">
                 Import Account</h1>
            </div>

            <div className="mb-6 md:mb-20">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">
                    <label className="text-sm md:text-2xl font-medium text-white">
                        Generate Import Template:
                    </label>

                    <button
                        onClick={handleGenerateTemplate}
                        className="
              w-full md:w-auto 
              px-4 md:px-6 py-2 md:py-3 
              bg-gray-700 text-white rounded-md 
              hover:bg-gray-600 transition-colors
            "
                    >
                        Generate Import Template
                    </button>
                </div>
            </div>
            <div className="mb-6 md:mb-8">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">

                    <label className="text-sm md:text-2xl font-medium text-white">
                        Choose Import File:
                    </label>

                    <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            accept=".xlsx,.xls"
                            className="hidden"
                        />

                        <button
                            onClick={handleChooseFile}
                            className="
                w-full md:w-auto 
                px-4 md:px-6 py-2 md:py-3 md:ml-20
                bg-gray-700 text-white rounded-md 
                hover:bg-gray-600 transition-colors
              "
                        >
                            Choose File
                        </button>

                        {selectedFile && (
                            <span className="text-sm md:text-base text-gray-400 truncate max-w-xs md:max-w-none">
                                {selectedFile.name}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <button
  onClick={handleImport}
  disabled={!selectedFile}
  className="
    w-full               /* Full width on mobile */
    sm:w-48              /* Small screens */
    md:w-60              /* Medium screens */
    lg:w-72              /* Large screens */
    py-2 md:py-3
    rounded-md font-medium
    bg-blue-600 text-white 
    disabled:bg-gray-500 disabled:cursor-not-allowed
    hover:bg-blue-700
    transition-colors md:ml-77
    focus:outline-none focus:ring-2 focus:ring-offset-2 
    focus:ring-offset-gray-900 focus:ring-blue-500
  "
>
  Import
</button>
        </div>
    );
};

export default ImportAccount;